import { useRef, useEffect } from "react";
import { cn } from "@pineseed/gui/util/utils";
import {
  User,
  LogOut,
  LogIn,
  ArrowLeft,
  Hash,
} from "lucide-react";
import { SearchCombo } from "@pineseed/gui/component/searchCombo";
import { ConnectionIndicator } from "@pineseed/gui/component/ConnectionIndicator";
import { ThemeToggle } from "@pineseed/gui/component/ThemeToggle";
import { AvatarDome } from "@pineseed/gui/component/AvatarDome";
import { VisualEffect, VisualEffectRef } from "@pineseed/gui/component";
import { Workspace, Tag } from "@/types";
import { type ConnectionStatus } from "@pineseed/gui/hook/useConnectionStatus";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";

export type AppHeaderProps = {
  className?: string;
  participant: { id: string; name: string; role: string };
  clearAuth: () => void;
  setActiveWorkspaceId: (workspaceId: string | null) => void;
  activeWorkspace: boolean;
  availableWorkspaces: string[];
  tagCount: number;
  activeWorkspaceValue: Workspace | null;
  allTags: Tag[];
  authentication?: {
    login: () => Promise<void>;
    logout: () => Promise<void>;
    isAuthenticated: boolean;
    authLoading: boolean;
  };
  connectionStatus: ConnectionStatus;
  baseUrl: string;
  showSearch?: boolean;
};

export function AppHeader(props: AppHeaderProps) {
  const {
    className,
    participant,
    clearAuth,
    setActiveWorkspaceId,
    activeWorkspace,
    availableWorkspaces,
    tagCount,
    activeWorkspaceValue,
    allTags,
    authentication,
    connectionStatus,
    baseUrl,
    showSearch,
  } = props;

  const doAuthentication = !!authentication;

  const handleSignout = async () => {
    if (doAuthentication && authentication) {
      await authentication.logout();
    }
    clearAuth();
  };

  const handleSignin = async () => {
    if (doAuthentication && authentication) {
      try {
        await authentication.login();
      } catch (error) {
        console.error("Popup login failed:", error);
        // Error is already handled by the auth handler and displayed via state
      }
    }
  };

  const handleBackToWorkspaceSelection = () => {
    setActiveWorkspaceId(null);
  };

  const displayName = `${participant.name.substring(0, 10)}${participant.name.length > 10 ? "..." : ""}`;
  const participantIdDisplay = `${participant.id.substring(0, 4)}${participant.id.substring(participant.id.length - 4)}`;
  const roleDisplay = ` (${participant.role})`;

  // Copy participant ID
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Visual effect ref for emoji burst
  const visualEffectRef = useRef<VisualEffectRef>(null);

  // Expose window.triggerEmojiBurst for debugging/testing
  useEffect(() => {
    (window as any).triggerEmojiBurst = (emoji: string = "🎉") => {
      console.log("Debug: Triggering emoji burst effect with", emoji);
      visualEffectRef.current?.trigger({ emoji });
    };
    return () => {
      delete (window as any).triggerEmojiBurst;
    };
  }, []);

  return (
    <div
      className={cn(
        "flex items-center border-b border-border bg-background h-8",
        className,
      )}
    >
      {/* Left section - Back button (only show when workspace is active) */}
      <div className="flex items-center">
        {activeWorkspace && availableWorkspaces.length > 1 && (
          <button
            onClick={handleBackToWorkspaceSelection}
            className="p-1 rounded hover:bg-muted flex items-center text-muted-foreground hover:text-foreground transition-colors mr-3"
            title="Back to workspace selection"
          >
            <ArrowLeft size={16} />
          </button>
        )}
      </div>

      <VisualEffect
        ref={visualEffectRef}
        emojiBurstProps={{
          duration: 1000,
          emojiCount: 24,
          startSize: 16,
          endSize: 64,
        }}
      >
        <AvatarDome
          facesBasePath={baseUrl}
          useFaceTracker={true}
          size={24}
          className="ml-2 mr-1 mt-1"
          onClick={(e) => {
            console.log("Avatar clicked!", e);
          }}
        />
      </VisualEffect>

      {/* Connection Indicator */}
      <ConnectionIndicator
        connectionStatus={connectionStatus}
        className="ml-1"
      />

      {/* Theme Toggle */}
      <ThemeToggle className="ml-2" />

      {/* Tag Count */}
      <TagCounter
        tagCount={tagCount}
        activeWorkspaceValue={activeWorkspaceValue}
        allTags={allTags}
      />

      {/* Search Bar */}
      {showSearch && (
        <div className="flex-shrink-0 w-1/3 max-w-md">
          <SearchCombo placeholder="Search..." />
        </div>
      )}

      {/* Right section - takes up remaining space and aligns content to the right */}
      <div className="flex-1 flex items-center justify-end space-x-3">
        {/* User Info */}
        <div className="flex items-center group">
          <div
            className="rounded-full bg-brand-500 flex items-center justify-center"
            style={{ width: "1.25rem", height: "1.25rem" }}
          >
            <User size={14} className="text-white" />
          </div>
          <span className="text-sm font-medium mx-1">
            {displayName} -{" "}
            <CopyableText
              id={participant.id}
              onCopy={copyToClipboard}
              isPulsing={isCopied(participant.id)}
              variant="primary"
              size="sm"
            >
              {participantIdDisplay}
            </CopyableText>{" "}
            : {roleDisplay}
          </span>
          {doAuthentication &&
            authentication &&
            (authentication.isAuthenticated ? (
              <button
                onClick={handleSignout}
                className="opacity-50 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded text-destructive/70 hover:text-destructive"
                title="Sign out"
              >
                <LogOut size={14} />
              </button>
            ) : (
              <button
                onClick={handleSignin}
                className="opacity-50 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded text-primary/70 hover:text-primary"
                title="Sign in"
                disabled={authentication.authLoading}
              >
                {authentication.authLoading ? "..." : <LogIn size={16} />}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

interface TagCounterProps {
  tagCount: number;
  activeWorkspaceValue: Workspace | null;
  allTags: Tag[];
}

function TagCounter({
  tagCount,
  activeWorkspaceValue,
  allTags,
}: TagCounterProps) {
  const activeWorkspace = activeWorkspaceValue;

  // Only show when workspace is active and has tags
  if (!activeWorkspace || tagCount === 0) {
    return null;
  }

  // Create simple tooltip text
  const tooltipText =
    allTags.length > 0
      ? `Workspace Tags (${tagCount}):\n${allTags.map((tag) => `• ${tag.name} (${tag.type})`).join("\n")}`
      : `${tagCount} tags available in the workspace`;

  return (
    <div
      className="flex items-center mx-1 text-xs text-muted-foreground cursor-help"
      title={tooltipText}
    >
      <Hash size={12} />
      <span>{tagCount}</span>
    </div>
  );
}
